import React from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, ShieldCheck } from "lucide-react";
import PageMeta from "../../components/seo/PageMeta";
import SocialLinks from "../../components/SocialLinks";
import { FEATURE_MAP, FEATURES } from "../../data/features";

const SITE = "https://academicexaminer.online";

function MyAELogo({ size = 32 }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="rounded-lg bg-indigo-600 flex items-center justify-center font-mono font-bold text-white shadow-sm" style={{ width: size, height: size, fontSize: size * 0.48 }}>Æ</div>
      <span className="font-display font-semibold text-zinc-900 tracking-tight" style={{ fontSize: size * 0.55 }}>My<span className="text-indigo-600">AE</span></span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-zinc-900">{title}</h2>
      <div className="mt-3 text-zinc-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function Bullets({ items, testId }) {
  return (
    <ul className="mt-2 space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 items-start">
          <Check size={17} className="text-indigo-500 shrink-0 mt-1" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function FeatureNav({ currentSlug }) {
  return (
    <nav className="text-xs text-zinc-500 flex flex-wrap gap-x-4 gap-y-1" aria-label="Breadcrumb">
      <Link to="/" className="hover:text-zinc-900">MyAE</Link>
      <ChevronRight size={12} className="mt-0.5" />
      <Link to="/features" className="hover:text-zinc-900">Features</Link>
      <ChevronRight size={12} className="mt-0.5" />
      <span className="text-zinc-900">{FEATURE_MAP[currentSlug]?.title}</span>
    </nav>
  );
}

export default function FeaturePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const feat = FEATURE_MAP[slug];
  if (!feat) return <Navigate to="/features" replace />;

  const canonical = `${SITE}/features/${feat.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "MyAE", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Features", item: SITE + "/features" },
        { "@type": "ListItem", position: 3, name: feat.title, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: feat.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `${feat.title} — MyAE`,
      description: feat.intro,
      url: canonical,
      isPartOf: { "@id": `${SITE}/#website` },
      about: { "@id": `${SITE}/#software` },
      inLanguage: "en-GB",
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900" data-testid={`feature-${feat.slug}`}>
      <PageMeta
        title={`${feat.title} — MyAE`}
        description={feat.intro}
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="cursor-pointer" data-testid="feature-logo"><MyAELogo size={30} /></button>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/features" className="text-zinc-600 hover:text-zinc-900">Features</Link>
            <Link to="/pricing" className="text-zinc-600 hover:text-zinc-900">Pricing</Link>
            <button onClick={() => navigate("/new")} className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-testid="feature-cta-header">Try MyAE</button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <FeatureNav currentSlug={feat.slug} />

        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-900 leading-[1.05]" data-testid="feature-title">{feat.title}</h1>
        <p className="mt-4 text-lg text-zinc-600 leading-relaxed" data-testid="feature-intro">{feat.intro}</p>

        <div className="mt-6 inline-flex items-center gap-2 text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 px-3 py-1.5 rounded-full">
          <ShieldCheck size={13} /> Free until further notice — no card required
        </div>

        <Section title="What it does">
          <p>{feat.what_it_does}</p>
        </Section>

        <Section title="How it works">
          <ol className="mt-2 space-y-2 list-decimal list-outside pl-5">
            {feat.how_it_works.map((step, i) => (
              <li key={i} className="text-[15px] leading-relaxed pl-1">{step}</li>
            ))}
          </ol>
        </Section>

        <Section title="What MyAE considers">
          <Bullets items={feat.considered} testId="feature-considered" />
        </Section>

        <Section title="What you receive">
          <Bullets items={feat.what_you_receive} testId="feature-receive" />
        </Section>

        <Section title="Limitations">
          <Bullets items={feat.limitations} testId="feature-limits" />
          <p className="mt-4 text-xs text-zinc-500">
            MyAE is a study-support tool operated by <b>OpenDegree Ltd</b> (registered in England and Wales, Company No. 17397971).
            It provides indicative, AI-assisted feedback and is not a substitute for your institution&apos;s marking.
          </p>
        </Section>

        <Section title="Frequently asked questions">
          <div className="mt-2 divide-y divide-zinc-200 border-y border-zinc-200" data-testid="feature-faq">
            {feat.faq.map((f, i) => (
              <details key={i} className="py-3 group">
                <summary className="cursor-pointer flex items-center justify-between text-[15px] font-medium text-zinc-900 list-none">
                  <span>{f.q}</span>
                  <ChevronRight size={16} className="text-zinc-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-2 text-[14px] text-zinc-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <div className="mt-12 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-6 sm:p-8" data-testid="feature-cta">
          <h3 className="font-display text-2xl font-medium tracking-tight text-zinc-900">Ready to try {feat.title}?</h3>
          <p className="mt-2 text-zinc-600">Upload your brief and current draft. It takes about a minute. Every plan is free while we grow.</p>
          <button onClick={() => navigate("/new")} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-indigo-700 transition-colors" data-testid="feature-cta-btn">
            Analyse an assignment <ArrowRight size={15} />
          </button>
        </div>

        {/* Related features (internal linking) */}
        <div className="mt-10">
          <p className="text-xs uppercase tracking-widest text-zinc-500">Related MyAE features</p>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {feat.related.map((sib) => {
              const f = FEATURE_MAP[sib];
              if (!f) return null;
              return (
                <Link key={sib} to={`/features/${sib}`} className="block rounded-lg border border-zinc-200 hover:border-indigo-300 bg-white p-4 transition-colors" data-testid={`feature-related-${sib}`}>
                  <p className="font-display text-lg text-zinc-900">{f.title}</p>
                  <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{f.intro}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <MyAELogo size={22} />
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/features" className="hover:text-zinc-900">Features</Link>
            <Link to="/pricing" className="hover:text-zinc-900">Pricing</Link>
            <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
            <Link to="/contact" className="hover:text-zinc-900">Contact</Link>
          </div>
          <SocialLinks className="basis-full sm:basis-auto" />
          <p className="text-zinc-400 basis-full sm:basis-auto">© {new Date().getFullYear()} OpenDegree Ltd</p>
        </div>
      </footer>
    </div>
  );
}
